class PremiumHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
    this.transitioning = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.premium-hero__slide'));
    this.panels = Array.from(this.querySelectorAll('.premium-hero__content-panel'));
    this.dots = Array.from(this.querySelectorAll('.premium-hero__dot'));
    this.pauseButton = this.querySelector('.premium-hero__pause');
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 10) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.slides.length === 0) return;

    this.prepareVideos();
    this.bindEvents();
    this.setActiveSlide(0, false);

    if (!this.isPaused && this.autoplayEnabled && !this.reducedMotion.matches) {
      this.startAutoplay();
    }

    this.updatePauseButton();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.unbindVideoEvents();
  }

  prepareVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      if (!video.getAttribute('preload')) {
        video.setAttribute('preload', 'auto');
      }
    });
  }

  bindEvents() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        if (!Number.isNaN(index)) this.goTo(index, true);
      });
    });

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => this.togglePause());
    }

    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) {
        this.stopAutoplay();
      } else if (!this.isPaused && this.autoplayEnabled) {
        this.startAutoplay();
      }
    });

    this.bindVideoEvents();
  }

  bindVideoEvents() {
    this.videoEndHandlers = this.videoEndHandlers || new Map();

    this.querySelectorAll('video').forEach((video) => {
      if (this.videoEndHandlers.has(video)) return;

      const handler = () => this.onVideoEnded(video);
      this.videoEndHandlers.set(video, handler);
      video.addEventListener('ended', handler);
    });
  }

  unbindVideoEvents() {
    if (!this.videoEndHandlers) return;

    this.videoEndHandlers.forEach((handler, video) => {
      video.removeEventListener('ended', handler);
    });
    this.videoEndHandlers.clear();
  }

  onVideoEnded(video) {
    if (this.isPaused || this.slides.length < 2) return;
    if (this.reducedMotion.matches) return;

    const slide = video.closest('.premium-hero__slide');
    if (!slide || !slide.classList.contains('is-active')) return;

    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goTo(nextIndex);
  }

  goTo(index, userInitiated = false) {
    if (index === this.currentIndex || this.transitioning || index < 0 || index >= this.slides.length) return;

    if (userInitiated && this.autoplayEnabled && !this.isPaused && !this.reducedMotion.matches) {
      this.restartAutoplay();
    }

    this.setActiveSlide(index, true);
  }

  setActiveSlide(index, animate) {
    this.transitioning = animate;
    this.currentIndex = index;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.pauseInactiveVideos(index);

    if (!this.isPaused) {
      this.startSlideVideo(index);
      if (!this.reducedMotion.matches) {
        this.restartAutoplay();
      }
    }

    window.setTimeout(() => {
      this.transitioning = false;
    }, animate ? 700 : 0);
  }

  pauseInactiveVideos(activeIndex) {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video || i === activeIndex) return;

      video.pause();
      video.currentTime = 0;
    });
  }

  pauseAllVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }

  startSlideVideo(index) {
    if (this.isPaused || this.currentIndex !== index) return;

    const video = this.slides[index]?.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.currentTime = 0;

    const play = () => {
      if (this.isPaused || this.currentIndex !== index) return;
      video.play().catch(() => {});
    };

    if (video.readyState >= 2) {
      play();
      return;
    }

    video.addEventListener('canplay', play, { once: true });
  }

  resumeSlideVideo(index) {
    if (this.isPaused || this.currentIndex !== index) return;

    const video = this.slides[index]?.querySelector('video');
    if (!video) return;

    video.muted = true;

    if (video.ended || video.currentTime === 0) {
      this.startSlideVideo(index);
      return;
    }

    video.play().catch(() => {});
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.isPaused || this.reducedMotion.matches) return;
    this.restartAutoplay();
  }

  restartAutoplay() {
    this.stopAutoplay();

    const slide = this.slides[this.currentIndex];
    const video = slide?.querySelector('video');

    if (video) return;

    this.autoplayTimer = window.setTimeout(() => {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goTo(nextIndex);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  togglePause() {
    this.setPaused(!this.isPaused);
  }

  setPaused(paused) {
    this.isPaused = paused;
    this.dataset.paused = paused ? 'true' : 'false';

    if (paused) {
      this.stopAutoplay();
      this.pauseAllVideos();
    } else {
      this.resumeSlideVideo(this.currentIndex);
      if (this.autoplayEnabled && !this.reducedMotion.matches) {
        this.startAutoplay();
      }
    }

    this.updatePauseButton();
  }

  updatePauseButton() {
    if (!this.pauseButton) return;

    const pauseLabel = this.pauseButton.dataset.pauseLabel;
    const playLabel = this.pauseButton.dataset.playLabel;
    const label = this.isPaused ? playLabel : pauseLabel;

    this.pauseButton.setAttribute('aria-label', label);
    this.pauseButton.setAttribute('aria-pressed', this.isPaused ? 'true' : 'false');
    this.pauseButton.classList.toggle('is-paused', this.isPaused);

    const text = this.pauseButton.querySelector('.premium-hero__pause-text');
    if (text) text.textContent = label;
  }
}

customElements.define('premium-hero-carousel', PremiumHeroCarousel);
